/**
 * LogoutConfirmModal.jsx
 *
 * Shared modal used for two scenarios:
 *
 *  mode="confirm"
 *    Shown when user clicks the Logout button manually.
 *    Two actions: Cancel | Logout
 *
 *  mode="idle"
 *    Shown when the auto-logout idle timer fires.
 *    Displays a live countdown. Two actions: Stay Logged In | Logout Now
 */
import React from 'react';

export default function LogoutConfirmModal({
  mode = 'confirm',   // 'confirm' | 'idle'
  countdown = 60,     // only used in idle mode
  onConfirm,          // () => void  — log out now
  onCancel,           // () => void  — stay / cancel
}) {
  const isIdle = mode === 'idle';

  // Colour the countdown ring based on urgency
  const urgencyColor =
    countdown > 30 ? 'text-amber-500' :
    countdown > 10 ? 'text-orange-500' :
    'text-red-600';

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="logout-modal-title"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={isIdle ? undefined : onCancel} // don't dismiss idle warning on backdrop click
      />

      {/* Card */}
      <div className="relative bg-white rounded-2xl shadow-2xl p-8 w-full max-w-sm mx-4 flex flex-col items-center gap-5">

        {/* Icon */}
        <div className={`w-14 h-14 rounded-full flex items-center justify-center text-2xl ${
          isIdle ? 'bg-amber-100' : 'bg-red-50'
        }`}>
          {isIdle ? '⏱' : '🔐'}
        </div>

        {/* Title */}
        <h2
          id="logout-modal-title"
          className="text-lg font-bold text-gray-900 text-center"
        >
          {isIdle ? 'Session Expiring Soon' : 'Confirm Logout'}
        </h2>

        {/* Body */}
        <p className="text-sm text-gray-500 text-center leading-relaxed">
          {isIdle ? (
            <>
              You have been inactive for a while.
              You will be automatically logged out in{' '}
              <span className={`font-bold text-base ${urgencyColor}`}>
                {countdown}s
              </span>
              .
            </>
          ) : (
            'Are you sure you want to log out? Any unsaved changes will be lost.'
          )}
        </p>

        {/* Countdown ring (idle only) */}
        {isIdle && (
          <div className="relative w-16 h-16">
            <svg className="w-full h-full -rotate-90" viewBox="0 0 64 64">
              {/* Track */}
              <circle cx="32" cy="32" r="28" fill="none" stroke="#e5e7eb" strokeWidth="5" />
              {/* Progress — shrinks as countdown decreases */}
              <circle
                cx="32" cy="32" r="28"
                fill="none"
                stroke={countdown > 30 ? '#f59e0b' : countdown > 10 ? '#f97316' : '#ef4444'}
                strokeWidth="5"
                strokeLinecap="round"
                strokeDasharray={`${2 * Math.PI * 28}`}
                strokeDashoffset={`${2 * Math.PI * 28 * (1 - countdown / 60)}`}
                style={{ transition: 'stroke-dashoffset 1s linear, stroke 0.5s' }}
              />
            </svg>
            <span className={`absolute inset-0 flex items-center justify-center text-lg font-bold ${urgencyColor}`}>
              {countdown}
            </span>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3 w-full">
          {/* Primary: stay / cancel */}
          <button
            type="button"
            onClick={onCancel}
            autoFocus={!isIdle}
            className="flex-1 py-2.5 rounded-xl border border-gray-200 bg-white text-gray-700 font-semibold text-sm hover:bg-gray-50 transition"
          >
            {isIdle ? 'Stay Logged In' : 'Cancel'}
          </button>

          {/* Destructive: logout */}
          <button
            type="button"
            onClick={onConfirm}
            autoFocus={isIdle}
            className="flex-1 py-2.5 rounded-xl bg-red-600 text-white font-semibold text-sm hover:bg-red-700 active:scale-[0.98] transition"
          >
            {isIdle ? 'Logout Now' : 'Logout'}
          </button>
        </div>
      </div>
    </div>
  );
}
